/**
 * Real-time Live Sync (Feb 2026).
 *
 * A singleton WebSocket connection that fans server events out to every
 * subscriber (BranchCalendar, OrderHeader, etc.) and also lets the
 * caller publish transient presence claims (perforated "someone is
 * trying to book here" squares).
 *
 * Auth: staff JWT read from localStorage. Never opens on a customer
 * token — the ws endpoint 1008-rejects those anyway.
 *
 * Auto-reconnect: back-off starts at 750ms and doubles up to 30s so
 * an intermittent WiFi drop recovers in the background without any
 * page reloads.
 */
import { STAFF_TOKEN_KEY } from "./api";

let ws = null;
let openPromise = null;
let backoffMs = 750;
let reconnectTimer = null;
let heartbeatTimer = null;
const listeners = new Map(); // eventType -> Set<callback>
let lastPresence = { presence: [] };
let selfConnectionId = null;

function wsUrl() {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws").replace(/\/$/, "");
  if (!base) return null;
  const token = encodeURIComponent(localStorage.getItem(STAFF_TOKEN_KEY) || "");
  return `${base}/api/ws/live?token=${token}`;
}

function emit(eventType, payload) {
  const bag = listeners.get(eventType);
  if (!bag) return;
  for (const cb of bag) {
    try { cb(payload); } catch (e) { /* swallow subscriber errors */ }
  }
}

function schedule_reconnect() {
  if (reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    ensureOpen();
  }, backoffMs);
  backoffMs = Math.min(30_000, Math.floor(backoffMs * 1.7));
}

function stop_heartbeat() {
  if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
}

function start_heartbeat() {
  stop_heartbeat();
  heartbeatTimer = setInterval(() => {
    try {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
    } catch { /* ignore */ }
  }, 25_000);
}

export function ensureOpen() {
  if (openPromise) return openPromise;
  const url = wsUrl();
  if (!url || !localStorage.getItem(STAFF_TOKEN_KEY)) return null;
  try {
    ws = new WebSocket(url);
  } catch (_) {
    schedule_reconnect();
    return null;
  }
  openPromise = new Promise((resolve) => {
    ws.addEventListener("open", () => {
      backoffMs = 750;
      start_heartbeat();
      resolve(ws);
      emit("connected", {});
    });
    ws.addEventListener("close", () => {
      stop_heartbeat();
      openPromise = null;
      ws = null;
      emit("disconnected", {});
      schedule_reconnect();
    });
    ws.addEventListener("error", () => {
      // `close` will fire next — reconnect there.
    });
    ws.addEventListener("message", (ev) => {
      let data;
      try { data = JSON.parse(ev.data); } catch { return; }
      const t = data?.type;
      const payload = data?.payload || {};
      if (t === "presence_state") {
        lastPresence = { presence: Array.isArray(payload.presence) ? payload.presence : [] };
      } else if (t === "hello") {
        selfConnectionId = payload.connection_id || null;
      }
      emit(t, payload);
    });
  });
  return openPromise;
}

export function subscribe(eventType, cb) {
  if (!listeners.has(eventType)) listeners.set(eventType, new Set());
  listeners.get(eventType).add(cb);
  ensureOpen();
  return () => {
    const bag = listeners.get(eventType);
    if (bag) bag.delete(cb);
  };
}

export function publishPresence(slot) {
  const send = (payload) => {
    try {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(payload));
    } catch { /* ignore */ }
  };
  if (!slot) {
    send({ type: "presence", active: false });
    return;
  }
  const cleaned = {
    key: String(slot.key || ""),
    branch_id: String(slot.branch_id || ""),
    staff_id: String(slot.staff_id || ""),
    start_iso: String(slot.start_iso || ""),
    end_iso: String(slot.end_iso || ""),
  };
  send({ type: "presence", active: true, slot: cleaned });
  ensureOpen();
}

export function getLastPresence() {
  return lastPresence;
}

export function getSelfConnectionId() {
  return selfConnectionId;
}

export function disconnect() {
  try {
    stop_heartbeat();
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    if (ws) ws.close();
  } finally {
    ws = null;
    openPromise = null;
    selfConnectionId = null;
    lastPresence = { presence: [] };
  }
}
